import { pdfjs } from 'react-pdf';
import { getVideoThumbnailAtSecond } from './videoThumbnail';

const THUMBNAIL_JPEG_QUALITY = 0.85;
const THUMBNAIL_MAX_EDGE = 512;

let pdfWorkerConfigured = false;

function ensurePdfWorker() {
  if (pdfWorkerConfigured) return;
  pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
  pdfWorkerConfigured = true;
}

function canvasToJpegBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', THUMBNAIL_JPEG_QUALITY);
  });
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image load failed'));
    };
    img.src = url;
  });
}

async function rasterizeImageFileToJpeg(file: File): Promise<Blob | null> {
  try {
    const img = await loadImageFromFile(file);
    const longest = Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height, 1);
    const scale = Math.min(1, THUMBNAIL_MAX_EDGE / longest);
    const width = Math.max(1, Math.round((img.naturalWidth || img.width) * scale));
    const height = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, width, height);
    return canvasToJpegBlob(canvas);
  } catch {
    return null;
  }
}

/** PDF ilk sayfasını JPEG thumbnail olarak üretir. */
export async function getPdfFirstPageThumbnail(file: File): Promise<Blob | null> {
  const isPdf =
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  if (!isPdf) return null;

  ensurePdfWorker();

  try {
    const data = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data }).promise;
    const page = await pdf.getPage(1);
    const baseViewport = page.getViewport({ scale: 1 });
    const longest = Math.max(baseViewport.width, baseViewport.height, 1);
    const scale = Math.min(1.5, THUMBNAIL_MAX_EDGE / longest);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    await page.render({ canvasContext: ctx, viewport, canvas }).promise;
    return canvasToJpegBlob(canvas);
  } catch {
    return null;
  }
}

/**
 * Yeni dosya upload'u için otomatik thumbnail: video ilk kare, image, PDF ilk sayfa.
 * Versiyon upload'unda kullanılmamalı.
 */
export async function resolveUploadThumbnail(file: File): Promise<Blob | undefined> {
  if (file.type.startsWith('video/')) {
    return (await getVideoThumbnailAtSecond(file, 0)) ?? undefined;
  }

  if (file.type.startsWith('image/')) {
    if (file.type === 'image/svg+xml') {
      return (await rasterizeImageFileToJpeg(file)) ?? undefined;
    }
    return file;
  }

  const pdfThumb = await getPdfFirstPageThumbnail(file);
  if (pdfThumb) return pdfThumb;

  return undefined;
}
